/**
 * Phase F / C-4 — Shared loading-message pools.
 *
 * 8 contexts × 15 strict-per-context strings = 120 unique pieces of
 * copy. Per Q18 + Q33 Claude authors all of them and they don't share
 * across contexts (strict per-context). Per `feedback_no_light_grey_text`
 * the renderer uses regular dark text on the surface; the strings
 * themselves stay tone-neutral so they look right anywhere.
 *
 * Each string must:
 *   - Be unique across the whole module (lint-checked).
 *   - Stay under 80 characters so it fits in one line of a status
 *     bar / tray cell / interstitial loader.
 *   - Sound warm, lawyer-respectful, occasionally legal-flavored.
 *     Never sound hype-y or salesy.
 *   - Avoid emojis (matches `feedback_use_tailwind`'s "no emoji unless
 *     the user requests it" rule).
 *
 * If a future tone-pass wants to swap copy in place, the array
 * positions are NOT semantically meaningful — `useLoadingMessage`
 * shuffles with a seeded RNG so the user perceives variety even on
 * repeat visits. The first 15 of any context are equivalent.
 *
 * Plan: ~/.claude/plans/it-s-time-to-rustling-locket.md §"Phase F" + C-4.
 */

export type LoadingContext =
  | 'graphRead'
  | 'graphWrite'
  | 'llmGen'
  | 'provision'
  | 'search'
  | 'sync'
  | 'nipeTool'
  | 'generic';

export const LOADING_POOLS: Record<LoadingContext, readonly string[]> = {
  // SharePoint / Graph READ side — fetching files, listing folders,
  // pulling metadata. Tone: receptionist-quiet, not technical.
  graphRead: [
    'Talking to SharePoint…',
    'Looking up your firm\'s documents…',
    'Reading from the matter folder…',
    'Pulling the latest metadata…',
    'Walking the document library…',
    'Checking who last touched this file…',
    'Loading version history from Graph…',
    'Asking SharePoint for the file list…',
    'Resolving the drive item id…',
    'Reading the document preview…',
    'Fetching the folder you opened…',
    'Pulling sensitivity labels…',
    'Looking up the sharing roster…',
    'Reading file permissions…',
    'Querying the SharePoint search index…',
  ],

  // Graph WRITE side — uploads, saves, permission changes. Tone:
  // confident, slightly more "doing something heavy".
  graphWrite: [
    'Saving back to SharePoint…',
    'Uploading the draft to your matter folder…',
    'Filing the new version in Graph…',
    'Stamping check-in metadata…',
    'Writing a new revision record…',
    'Replacing the document with your latest edit…',
    'Persisting the change to SharePoint…',
    'Updating the file you just edited…',
    'Committing the upload to the drive…',
    'Refreshing SharePoint with your changes…',
    'Recording the save in the audit log…',
    'Locking the file briefly while we write…',
    'Releasing the file lock now that we\'re done…',
    'Pushing the new bytes upstream…',
    'Synchronizing your draft with SharePoint…',
  ],

  // LLM generation — Prompt Lab, NIPE chat, Skill runs. Tone: a
  // lawyer-shaped take on "thinking". Never "consulting the AI".
  llmGen: [
    'Conferring with the model…',
    'Drafting your response…',
    'Citing the authorities you taught me…',
    'Reviewing precedent in your matters…',
    'Outlining the argument…',
    'Cross-checking facts against the file…',
    'Composing the next paragraph…',
    'Polishing the phrasing…',
    'Sanity-checking the citation format…',
    'Refining tone for opposing counsel…',
    'Picking the right verb for paragraph three…',
    'Marshalling the relevant exhibits…',
    'Letting the model breathe for a beat…',
    'Considering an alternative framing…',
    'Re-reading what you wrote before I answer…',
  ],

  // Portal provisioning — first-sign-in ACA Job kicks. Tone: warm,
  // lightly aware that "your portal" is being built right now.
  provision: [
    'Drafting the engagement letter for your portal…',
    'Conflict-checking your tenant…',
    'Reserving SharePoint storage for your firm…',
    'Spinning up your private workspace…',
    'Cloning the Morrison Legal demo matters…',
    'Wiring up your Skills library…',
    'Configuring your portal permissions…',
    'Asking Azure to provision a fresh site…',
    'Stamping your branding on the title bar…',
    'Indexing the demo corpus for fast search…',
    'Seeding your saved Bases views…',
    'Filing your matters into the new drive…',
    'Connecting the audit log to your portal…',
    'Almost there — last few wiring steps…',
    'Polishing the workspace before we hand you the keys…',
  ],

  // Corpus + Azure Search — full-text queries, hybrid search.
  // Tone: indexer-quiet, no jargon.
  search: [
    'Searching the corpus…',
    'Ranking matches by relevance…',
    'Combing through your documents…',
    'Looking across every matter at once…',
    'Embedding your query for semantic search…',
    'Asking Azure Search for the top hits…',
    'Sorting by recency and weight…',
    'Filtering by your scope picker…',
    'Pulling snippets from the matching files…',
    'Highlighting your terms in context…',
    'De-duplicating overlapping hits…',
    'Counting the matches before we render…',
    'Walking the per-portal index…',
    'Reading the search result page…',
    'Checking the hybrid-search blend ratio…',
  ],

  // Background sync — vault sync, MSAL refresh, queue drain.
  // Tone: background-process honest.
  sync: [
    'Syncing your vault to Convex…',
    'Pushing pending edits to SharePoint…',
    'Refreshing your Microsoft session…',
    'Catching up on what changed offline…',
    'Replaying queued saves from your last session…',
    'Comparing your local vault to the cloud copy…',
    'Reconciling note edits with the server…',
    'Updating the SharePoint cache…',
    'Draining the local edit queue…',
    'Re-issuing the access token before it expires…',
    'Bringing your devices into agreement…',
    'Healing a small clock drift…',
    'Re-attaching to the Graph after a hiccup…',
    'Mirroring your latest folder change…',
    'Stitching everything back together…',
  ],

  // NIPE tool runs — the Office-Assistant-style agent calling
  // getSkill / runSkill / listMatters. Tone: companion-voice.
  nipeTool: [
    'Looking up the Skill you asked about…',
    'Running the Skill against your document…',
    'Fetching the matter you mentioned…',
    'Reading your client profile for context…',
    'Pulling the right prompt template…',
    'Loading the Skill body from your library…',
    'Checking which version of the Skill is active…',
    'Hooking up the tool to your portal…',
    'Routing the request through the audit log…',
    'Confirming the action falls inside scope…',
    'Gathering everything I need before I answer…',
    'Walking your vault for the cited note…',
    'Reading the Skill\'s allowed-tools list…',
    'Picking up the conversation where we left off…',
    'Threading your context into the tool call…',
  ],

  // Generic catch-all — when the surface can't classify.
  // Tone: friendly fallback.
  generic: [
    'Working on it…',
    'One moment please…',
    'Just a beat…',
    'Putting the pieces together…',
    'Almost ready…',
    'Hold tight…',
    'Wrapping up…',
    'Tying off the last thread…',
    'Catching my breath…',
    'Counting to three on your behalf…',
    'Let me check that for you…',
    'Reading the brief before I respond…',
    'Sharpening the pencil…',
    'Lining up the next step…',
    'Settling into the answer…',
  ],
};

/**
 * Compile-time invariant: every context has 15 strings. Vitest
 * codifies this; ESLint won't catch it.
 */
export const REQUIRED_POOL_SIZE = 15;

/**
 * Public list of contexts in a stable iteration order. Useful for
 * the smoke harness + tests that want to walk every pool.
 */
export const LOADING_CONTEXTS: readonly LoadingContext[] = [
  'graphRead',
  'graphWrite',
  'llmGen',
  'provision',
  'search',
  'sync',
  'nipeTool',
  'generic',
];
